<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { canDecreaseModifier, canIncreaseModifier } from "#/hooks/useComboModifiers/canAmount";
import { useCardStore } from "#/store/card";
import { useMainStore } from "#/store/main";
import { useThemeStore } from "#/store/theme";
import type { IGroup } from "#/types/iMain";
import { formatPrice, getLocaleName } from "#/utils/i18next";
import AnimationCard from "../../../hooks/useAnimationLoader/AnimationLoader.vue";
import type { IProduct } from "../../../types/iCardProduct";
import Loader from "../../../ui/loader/Loader.vue";

const props = defineProps<{
	data: IProduct | IGroup;
	activeTags?: unknown[];
	is_product?: boolean;
	is_group?: boolean;
	parent?: string;
	isLoading?: boolean;
	selectGroupChild?: (item: any) => void;
	takeCurrentCard?: (id: number) => void;
	onAddToCart?: (item: IProduct) => void;
	maxAmount?: number;
	groupMaxAmount?: number;
	currentGroupAmount?: number;
	onAmountChange?: (amount: number) => void;
	amount?: number;
	groupId?: string;
}>();

const emit = defineEmits<{
	(e: "amount-change", amount: number): void;
	(e: "update:amount", amount: number): void;
}>();

const { i18next } = useTranslation();

const themeStore = useThemeStore();
const storeMain = useMainStore();
const { is_18_above_content, is18Allowed } = storeToRefs(storeMain);

const currentAmount = computed(() => props.amount ?? 0);

const basePrice = computed(() => Number((props.data as any)?.price) || 0);

const displayPrice = computed(() => {
	const price = basePrice.value;
	return price * currentAmount.value;
});

const shouldDisplayByTags = computed(() => {
	const activeTags = props.activeTags ?? [];
	if (activeTags.length === 0) return true;

	const itemTags = (props.data as any).tags;
	if (!itemTags || !Array.isArray(itemTags) || itemTags.length === 0) return false;

	return activeTags.some((activeTag: any) => itemTags.includes(activeTag));
});

const comboMaxAmount = computed(() => {
	if (props.maxAmount !== undefined && props.maxAmount !== null && props.maxAmount > 0) {
		return props.maxAmount;
	}
	const fromData = (props.data as any).max_amount;
	return fromData > 0 ? fromData : 0;
});

const modifierMinAmount = computed(() => {
	return (props.data as any).min_amount ?? 0;
});

const comboMinAmount = computed(() => {
	const fromData = (props.data as any).min_amount;
	return fromData > 0 ? fromData : 0;
});

const canDecrease = computed(() => {
	if (modifierMinAmount.value > 0) {
		return currentAmount.value > modifierMinAmount.value;
	}

	return canDecreaseModifier(currentAmount.value, modifierMinAmount.value);
});

const canIncrease = computed(() => canIncreaseModifier(props.data as any, currentAmount.value, props.groupMaxAmount, props.currentGroupAmount));

const updateAmount = (newAmount: number) => {
	emit("amount-change", newAmount);
	emit("update:amount", newAmount);
	if (props.onAmountChange) props.onAmountChange(newAmount);
};

const handlePlus = () => {
	if (!canIncrease.value) return;
	updateAmount(currentAmount.value + 1);
};

const handleMinus = () => {
	if (!canDecrease.value) return;

	const newAmount = Math.max(modifierMinAmount.value, currentAmount.value - 1);

	updateAmount(newAmount);
};

const isModifierSelected = computed(() => currentAmount.value > 0);

const handleCardClick = (item: any) => {
	if (props.is_group && props.selectGroupChild) {
		item = { ...item, parent: props.parent, child: item.child_groups };
		props.selectGroupChild(item);
		return;
	}
	if (item.adult && !is18Allowed.value) {
		is_18_above_content.value = true;
		return;
	}
	if (props.onAddToCart) {
		props.onAddToCart(item);
		return;
	}
	if (props.takeCurrentCard) props.takeCurrentCard(item.id);
};
</script>

<template>
	<div v-if="shouldDisplayByTags">
		<AnimationCard :key="props.data.id">
			<template #opacityLoader>
				<div class="modifiers_combo__wrapper">
					<div v-if="currentAmount > 0 && !is_group" class="modifiers_combo_amount_badge">{{ currentAmount }}</div>
					<div v-if="is_group" class="folder_content_top" />
					<div
						@click="handleCardClick(props.data)"
						class="modifiers_combo"
						:class="{
              modifiers_combo_folder: is_group,
              modifiers_combo_active: isModifierSelected,
            }"
					>
						<div v-if="isLoading" class="loader_modifiers_combo">
							<Loader />
						</div>
						<div v-else class="modifiers_combo_content">
							<img
								:src="props.data.image_link || themeStore.data?.fallback_image || ''"
								:class="['img', { 'card_blur_image': props.data.adult && !is18Allowed }]"
								loading="lazy"
								:alt="data.name"
							>
							<div class="modifiers_combo_title_price_wrapper">
								<p class="title">{{ getLocaleName(data, i18next.language) }}</p>
								<div v-if="!is_group && currentAmount === 0" class="modifiers_combo_price_row">
									<p class="price">+ {{ formatPrice(basePrice, storeMain.config_lib.currency_symbol) }}</p>
									<div class="add_button_combo" :class="{ add_button_combo_disabled: !canIncrease }" @click.stop="handlePlus">
										<div class="image_add_button" />
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						class="modifiers_combo_button_add_wrapper"
						:class="{ 'modifiers_combo_button_add_wrapper_centered': currentAmount > 0 }"
						v-if="!is_group && currentAmount > 0"
					>
						<div class="modifiers_combo_counter">
							<button
								type="button"
								class="modifiers_combo_counter_btn modifiers_combo_counter_btn_minus"
								:disabled="!canDecrease"
								:class="{ 'modifiers_combo_counter_btn_disabled': !canDecrease }"
								@click.stop="handleMinus"
							>
								-
							</button>
							<span class="modifiers_combo_counter_price">{{ formatPrice(displayPrice, storeMain.config_lib?.currency_symbol) }}</span>
							<button
								type="button"
								class="modifiers_combo_counter_btn modifiers_combo_counter_btn_plus"
								:disabled="!canIncrease"
								:class="{ modifiers_combo_counter_btn_disabled: !canIncrease }"
								@click.stop="handlePlus"
							>
								+
							</button>
						</div>
					</div>
				</div>
			</template>
		</AnimationCard>
	</div>
</template>

<style scoped lang="scss">
  @use './modifiersCombo.scss';
</style>